import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';
import { useOutletContext } from 'react-router-dom';

import { PLACEHOLDER_IMAGE } from 'constants/shop';
import { usePromoCodeContext } from 'contexts/PromoCodeContext';
import Button from 'honeydew/Button';
import ThumbnailCarousel from 'honeydew/ThumbnailCarousel';
import useTailwindTheme from 'hooks/useTailwindTheme';
import ProductTag from 'shared/ProductTag';
import ProductTags from 'shared/ProductTags';
import { trackAcquisitionPDPCTAClick } from 'utils/analyticsUtils';
import { getProductPhotos } from 'utils/productUtils';

import DetailsAccordion from './DetailsAccordion';
import ProductPrice from './ProductPrice';
import ProspectModule from './ProspectModule';

const PRICING_DISCLAIMER_COPY =
    '*Actual price may vary from week to week based on your delivery location and other factors. Listed price reflects the average price of this product across all locations for the current week. Products subject to availability.';

function AcquisitionPDP() {
    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });
    const { productData } = useOutletContext();

    const productPhotos = getProductPhotos(productData);

    const onSignUpCTAClick = () => {
        trackAcquisitionPDPCTAClick({
            productName: productData.name,
            productBrand: productData.brand,
        });
    };

    const thumbnailCarouselImages = productPhotos?.map((productPhoto) => ({
        src: productPhoto,
        alt: productData.name,
        width: '1500',
        height: '1500',
    }));

    const showProductTag =
        productData.bestSeller ||
        productData.exitSku ||
        productData.isGift ||
        productData.new ||
        productData.sale ||
        productData.soldOut ||
        false;

    const pageProps = {
        productData,
        showProductTag,
        thumbnailCarouselImages,
        onSignUpCTAClick,
    };

    return mobile ? <MobileAcquisitionPDP {...pageProps} /> : <DesktopAcquisitionPDP {...pageProps} />;
}

function MobileAcquisitionPDP({ onSignUpCTAClick, productData, showProductTag, thumbnailCarouselImages }) {
    const { renderSignupNanoBanner } = usePromoCodeContext();

    return (
        <main
            className={clsx('bg-white', {
                'pt-[160px]': renderSignupNanoBanner,
                'pt-[100px]': !renderSignupNanoBanner,
            })}
        >
            {productData.brand && <h3 className="text-body-sm mb-5 px-10 font-grotesk-bold">{productData.brand}</h3>}
            <h1 className="text-heading-sm mb-10 px-10">{productData.name}</h1>
            <div className="mx-10 my-15">
                <ProductPrice productData={productData} />
            </div>
            <div className="relative">
                {thumbnailCarouselImages.length > 0 ? (
                    <ThumbnailCarousel isMisfits images={thumbnailCarouselImages} />
                ) : (
                    <img src={PLACEHOLDER_IMAGE} width="500" height="500" alt="" aria-hidden="true" />
                )}
                {showProductTag && (
                    <div className="absolute left-10 top-10">
                        <ProductTag
                            bestSeller={productData.bestSeller}
                            exitSku={productData.exitSku}
                            isGift={productData.isGift}
                            newProduct={productData.new}
                            sale={productData.sale}
                            soldOut={productData.soldOut}
                        />
                    </div>
                )}
                <p className="text-body-sm mx-10 mt-10 bg-malt p-5">{PRICING_DISCLAIMER_COPY}</p>
            </div>
            <div className="flex flex-col px-10">
                <ProductTags sourcingReason={productData.sourcingReason} attributes={productData.attributes} />
                <DetailsAccordion
                    allergens={productData.allergens}
                    desc={productData.desc}
                    ingredients={productData.ingredients}
                    photoNutrition={productData.photoNutrition}
                />
            </div>
            <ProspectModule />
            <div className="fixed bottom-0 z-50 w-full bg-white p-10">
                <Button size="lg" as="link" to="/join" onClick={onSignUpCTAClick}>
                    Sign Up to Shop
                </Button>
            </div>
        </main>
    );
}

MobileAcquisitionPDP.propTypes = {
    onSignUpCTAClick: PropTypes.func.isRequired,
    productData: PropTypes.shape({}).isRequired,
    showProductTag: PropTypes.bool.isRequired,
    thumbnailCarouselImages: PropTypes.arrayOf(PropTypes.shape({ alt: PropTypes.string, src: PropTypes.string }))
        .isRequired,
};

function DesktopAcquisitionPDP({ onSignUpCTAClick, productData, showProductTag, thumbnailCarouselImages }) {
    const { renderSignupNanoBanner } = usePromoCodeContext();

    return (
        <main
            className={clsx('min-h-screen bg-white', {
                'pt-[140px]': renderSignupNanoBanner,
                'pt-[80px]': !renderSignupNanoBanner,
            })}
        >
            <div className="grid max-w-[900px] grow grid-cols-2 lg:mx-auto">
                <div className="col-span-1 border-r border-off-white px-20 pt-30">
                    <div className="relative mt-20">
                        {thumbnailCarouselImages.length > 0 ? (
                            <ThumbnailCarousel isMisfits images={thumbnailCarouselImages} />
                        ) : (
                            <img src={PLACEHOLDER_IMAGE} width="500" height="500" alt="" aria-hidden="true" />
                        )}
                        {showProductTag && (
                            <div className="absolute left-10 top-10">
                                <ProductTag
                                    bestSeller={productData.bestSeller}
                                    exitSku={productData.exitSku}
                                    isGift={productData.isGift}
                                    newProduct={productData.new}
                                    sale={productData.sale}
                                    soldOut={productData.soldOut}
                                />
                            </div>
                        )}
                    </div>
                    <DetailsAccordion
                        allergens={productData.allergens}
                        desc={productData.desc}
                        ingredients={productData.ingredients}
                        photoNutrition={productData.photoNutrition}
                    />
                </div>
                <div className="col-span-1 px-20 pt-[76px]">
                    <div className="sticky top-[156px] pb-40">
                        {productData.brand && <h3 className="mb-10 font-grotesk-bold">{productData.brand}</h3>}
                        <h1 className="text-heading-sm">{productData.name}</h1>
                        <ProductPrice productData={productData} />
                        <div className="mt-30">
                            <Button size="lg" as="link" to="/join" onClick={onSignUpCTAClick}>
                                Sign Up to Shop
                            </Button>
                        </div>
                        <ProductTags
                            sourcingReason={productData.sourcingReason}
                            attributes={productData.attributes}
                            acquisitionPDP
                        />
                        <p className="text-body-sm mt-30 bg-malt p-5">{PRICING_DISCLAIMER_COPY}</p>
                    </div>
                </div>
            </div>
            <ProspectModule renderSignUpCTA onSignUpCTAClick={onSignUpCTAClick} />
        </main>
    );
}

DesktopAcquisitionPDP.propTypes = {
    onSignUpCTAClick: PropTypes.func.isRequired,
    productData: PropTypes.shape({}).isRequired,
    showProductTag: PropTypes.bool.isRequired,
    thumbnailCarouselImages: PropTypes.arrayOf(PropTypes.shape({ alt: PropTypes.string, src: PropTypes.string }))
        .isRequired,
};

export { AcquisitionPDP as Component };
